import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useSEO } from "../lib/seo";
import { CheckCircle2, XCircle, Trophy, Target, Sparkles, ArrowRight, Flame, Share2, Twitter, Linkedin } from "lucide-react";
import { toast } from "sonner";

export default function Results() {
  const { attemptId } = useParams();
  const [a, setA] = useState(null);
  const [tab, setTab] = useState("summary");

  useEffect(() => {
    api.get(`/attempts/${attemptId}`).then((r) => setA(r.data)).catch(() => {});
  }, [attemptId]);

  useSEO({
    title: a ? `${a.score}% on practice mock — ${a.passed ? "Passed" : "Keep going"}` : "Your results",
    noindex: true,
  });

  if (!a) return <div className="container-x py-16" data-testid="results-loading">Loading results…</div>;

  const total = a.total || (a.review?.length || 0);
  const wrong = (a.review || []).filter((r) => !r.is_correct);

  const shareText = `I just scored ${a.score}% on a BeforeExam practice mock — pass probability ${a.pass_probability}%. Beat my score 👇`;
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/marketplace` : "";

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "My BeforeExam scorecard", text: shareText, url: shareUrl });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success("Scorecard copied — paste anywhere!");
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };
  const shareTwitter = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const shareLinkedIn = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="container-x py-10" data-testid="results-page">
      {/* Header */}
      <div className="rounded-2xl border border-border bg-card p-8 relative overflow-hidden">
        <div className="absolute inset-0 hero-mesh opacity-60 pointer-events-none" />
        <div className="relative grid sm:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider ${
              a.passed ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" : "bg-rose-500/15 text-rose-700 dark:text-rose-400"
            }`}>
              {a.passed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
              {a.passed ? "Passed" : "Not yet — keep going"}
            </div>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter">
              {a.score}% <span className="text-muted-foreground text-2xl">/ 100</span>
            </h1>
            <p className="mt-2 text-muted-foreground">
              {a.correct} of {total} correct · time: {Math.floor((a.time_taken_seconds || 0) / 60)}m
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 max-w-md">
            <div className="rounded-lg border border-border p-3 bg-background">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Pass prob</div>
              <div className="font-display text-2xl mt-1">{a.pass_probability}%</div>
            </div>
            <div className="rounded-lg border border-border p-3 bg-background">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Confidence</div>
              <div className="font-display text-2xl mt-1">{a.confidence_score}%</div>
            </div>
            <div className="rounded-lg border border-border p-3 bg-background">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">XP earned</div>
              <div className="font-display text-2xl mt-1 text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                <Trophy className="h-4 w-4" />+{a.earned_xp ?? "—"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex items-center gap-2 border-b border-border">
        {[
          { id: "summary", label: "Summary" },
          { id: "topics", label: `Topics (${(a.topic_stats || []).length})` },
          { id: "review", label: `Review (${a.review?.length || 0})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 ${
              tab === t.id ? "border-[color:var(--brand-blue)] text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "summary" && (
        <div className="mt-8 grid md:grid-cols-3 gap-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <Target className="h-6 w-6 text-[color:var(--brand-flame)]" />
            <h3 className="mt-3 font-display text-lg">What to study next</h3>
            <ul className="mt-3 space-y-2 text-sm">
              {(a.topic_stats || []).slice(0, 3).map((t) => (
                <li key={t.topic} className="flex justify-between"><span>{t.topic}</span><span className="text-muted-foreground">{t.score}%</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <Sparkles className="h-6 w-6 text-[color:var(--brand-blue)]" />
            <h3 className="mt-3 font-display text-lg">{a.mode === "mistakes" ? "Mistake drill update" : "Smart retest plan"}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {a.mode === "mistakes" ? "Correct answers were cleared from your mistake bank. Revisit any remaining misses after reviewing the explanations." : <>Retake this mock in <b className="text-foreground">3 days</b> after reviewing your weak topics. Your streak will keep compounding.</>}
            </p>
          </div>
          <div className="rounded-xl border border-border bg-card p-6">
            <Flame className="h-6 w-6 text-[color:var(--brand-flame)]" />
            <h3 className="mt-3 font-display text-lg">Streak update</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              You're on a <b className="text-foreground">{a.new_streak ?? 1}-day streak</b>. Keep going to unlock the 7-day badge.
            </p>
          </div>
        </div>
      )}

      {tab === "topics" && (
        <div className="mt-8 space-y-3">
          {(a.topic_stats || []).map((t) => (
            <div key={t.topic} className="rounded-lg border border-border bg-card p-5">
              <div className="flex justify-between text-sm">
                <span className="font-semibold">{t.topic}</span>
                <span className="text-muted-foreground">{t.score}% ({t.total} questions)</span>
              </div>
              <div className="mt-2 h-2 w-full rounded bg-muted overflow-hidden">
                <div
                  className={`h-full ${t.score >= 75 ? "bg-emerald-500" : t.score >= 50 ? "bg-yellow-500" : "bg-rose-500"}`}
                  style={{ width: `${t.score}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "review" && (
        <div className="mt-8 space-y-4">
          {(a.review || []).map((r, i) => (
            <div key={r.question_id} className="rounded-xl border border-border bg-card p-6">
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded bg-muted px-2 py-0.5 font-semibold">Q{i + 1}</span>
                <span className="text-muted-foreground">{r.topic}</span>
                {r.is_correct ? (
                  <span className="ml-auto inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Correct</span>
                ) : (
                  <span className="ml-auto inline-flex items-center gap-1 text-rose-600"><XCircle className="h-4 w-4" /> Incorrect</span>
                )}
              </div>
              <p className="mt-3 font-display text-base leading-snug">{r.text}</p>
              <div className="mt-4 space-y-2">
                {r.options.map((opt, k) => {
                  const isCorrect = k === r.correct_index;
                  const isSelected = k === r.selected_index;
                  return (
                    <div
                      key={k}
                      className={`rounded-md border p-3 text-sm flex items-start gap-3 ${
                        isCorrect ? "border-emerald-500/60 bg-emerald-500/5" :
                        isSelected ? "border-rose-500/60 bg-rose-500/5" :
                        "border-border"
                      }`}
                    >
                      <div className="font-bold text-xs w-5 mt-0.5">{String.fromCharCode(65 + k)}.</div>
                      <span>{opt}</span>
                      {isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-600 ml-auto" />}
                      {!isCorrect && isSelected && <XCircle className="h-4 w-4 text-rose-600 ml-auto" />}
                    </div>
                  );
                })}
              </div>
              {r.explanation && (
                <div className="mt-4 rounded-md border border-border bg-muted/50 p-3 text-sm">
                  <div className="text-[11px] uppercase font-bold tracking-wider text-muted-foreground mb-1">Explanation</div>
                  {r.explanation}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/dashboard"><button className="btn-ghost">Back to dashboard</button></Link>
        <Link to={`/practice/${a.course_id}/${a.test_number}?mode=${a.mode || "practice"}`}>
          <button className="btn-primary">Retake mock <ArrowRight className="h-4 w-4" /></button>
        </Link>
        <button onClick={share} className="btn-ghost" data-testid="share-scorecard"><Share2 className="h-4 w-4" /> Share scorecard</button>
        <a href={shareTwitter} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="share-twitter"><Twitter className="h-4 w-4" /> X/Twitter</a>
        <a href={shareLinkedIn} target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="share-linkedin"><Linkedin className="h-4 w-4" /> LinkedIn</a>
      </div>
    </div>
  );
}
